import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, MapPin, Phone } from "lucide-react";

const DAY_LABELS = [["mo", "Montag"], ["di", "Dienstag"], ["mi", "Mittwoch"], ["do", "Donnerstag"], ["fr", "Freitag"], ["sa", "Samstag"], ["so", "Sonntag"]];
const empty = {
  name: "", address: "", city: "", zip: "", phone: "", color: "#1D4ED8",
  opening_hours: { mo: "08:00-18:30", di: "08:00-18:30", mi: "08:00-18:30", do: "08:00-18:30", fr: "08:00-18:30", sa: "08:00-14:00", so: "" },
  active: true, latitude: null, longitude: null,
};

export default function LocationsPage() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/locations")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (dialog === "create") await api.post("/locations", form);
      else await api.put(`/locations/${form.id}`, form);
      toast.success("Gespeichert");
      setDialog(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Löschen?")) return;
    try { await api.delete(`/locations/${id}`); toast.success("Gelöscht"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-6" data-testid="locations-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Verwaltung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Standorte</h2>
        </div>
        <Button data-testid="loc-add-btn" onClick={() => { setForm(empty); setDialog("create"); }} className="rounded-full">
          <Plus className="w-4 h-4 mr-2" /> Standort hinzufügen
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((l) => (
          <Card key={l.id} data-testid={`loc-card-${l.id}`} className="card-hover border-border/60">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: `${l.color}20`, color: l.color }}>
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`loc-edit-${l.id}`} size="icon" variant="ghost" onClick={() => { setForm(l); setDialog("edit"); }}><Pencil className="w-4 h-4" /></Button>
                  <Button data-testid={`loc-delete-${l.id}`} size="icon" variant="ghost" onClick={() => remove(l.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </div>
              </div>
              <h3 className="font-heading text-xl font-bold mb-1">{l.name}</h3>
              <div className="text-sm text-slate-500 flex items-center gap-1.5 mb-1"><MapPin className="w-3.5 h-3.5" /> {l.address}, {l.zip} {l.city}</div>
              {l.phone && <div className="text-sm text-slate-500 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {l.phone}</div>}
              <div className="mt-3 pt-3 border-t border-border/60 text-xs text-slate-500 space-y-0.5">
                {DAY_LABELS.map(([k, lbl]) => (
                  <div key={k} className="flex justify-between">
                    <span>{lbl}</span>
                    <span className="tabular-nums">{l.opening_hours?.[k] || "geschlossen"}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dialog === "create" ? "Standort anlegen" : "Standort bearbeiten"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input data-testid="loc-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2"><Label>Adresse</Label><Input data-testid="loc-form-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>PLZ</Label><Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
            <div><Label>Stadt</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Farbe</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 p-1" /></div>
            <div><Label>Latitude (für Botendienst)</Label><Input type="number" step="0.000001" placeholder="51.2277" value={form.latitude ?? ""} onChange={(e) => setForm({ ...form, latitude: e.target.value ? parseFloat(e.target.value) : null })} /></div>
            <div><Label>Longitude</Label><Input type="number" step="0.000001" placeholder="6.7735" value={form.longitude ?? ""} onChange={(e) => setForm({ ...form, longitude: e.target.value ? parseFloat(e.target.value) : null })} /></div>
            <div className="col-span-2">
              <Label className="mb-2 block">Öffnungszeiten</Label>
              <div className="space-y-1">
                {DAY_LABELS.map(([k, lbl]) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-24 text-sm">{lbl}</span>
                    <Input placeholder="z.B. 08:00-18:30" value={form.opening_hours?.[k] || ""} onChange={(e) => setForm({ ...form, opening_hours: { ...form.opening_hours, [k]: e.target.value } })} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button data-testid="loc-form-save" onClick={save}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
